import PauseCircleIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import { Card, CardContent, Typography } from "@mui/material";
import { Box } from "@mui/system";
import type { UseMutationResult } from "@tanstack/react-query";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "@/theme/tokens";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { AgentStaffActionStatus, type FlatNurseAction } from "@/utils/models";

interface MobileActionsTitleProps {
	neededNurses?: number;
	agentStatus?: AgentStaffActionStatus;
	pauseByRequestMutation: UseMutationResult<void, Error, void>;
	resumeByRequestMutation: UseMutationResult<void, Error, void>;
	isAnyMutationPending: boolean;
	isLoadingMetadata: boolean;
	allActions: FlatNurseAction[];
}

export const MobileActionsTitle: FC<MobileActionsTitleProps> = ({
	neededNurses,
	agentStatus,
	pauseByRequestMutation,
	resumeByRequestMutation,
	isAnyMutationPending,
	isLoadingMetadata,
	allActions,
}) => {
	const { t } = useTranslation();
	const isRequestPaused = agentStatus === AgentStaffActionStatus.Paused;
	const isAgentDone = agentStatus === AgentStaffActionStatus.Completed;
	const isNotInitiated = agentStatus === AgentStaffActionStatus.NotInitiated;

	const approvedNurseIds = Array.from(new Set(allActions.filter((action) => action.is_approved).map((action) => action.nurse_id)));
	const isAccomplished = neededNurses !== undefined && approvedNurseIds.length === neededNurses;

	const statusBox =
		isAgentDone || isNotInitiated ? null : !isRequestPaused ? (
			<SystemButton
				buttonVariant="borderless"
				onClick={() => pauseByRequestMutation.mutate()}
				disabled={isAnyMutationPending || isLoadingMetadata}
				loading={pauseByRequestMutation.isPending}
				leadingIcon={<PauseCircleIcon />}
			/>
		) : (
			<SystemButton
				buttonVariant="borderless"
				onClick={() => resumeByRequestMutation.mutate()}
				disabled={isAnyMutationPending || isLoadingMetadata}
				loading={resumeByRequestMutation.isPending}
				leadingIcon={<PlayCircleOutlineIcon />}
			/>
		);

	const titleText = useMemo(() => {
		switch (agentStatus) {
			case AgentStaffActionStatus.Completed:
				return (
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px", alignSelf: "center" }}>
						Done {isAccomplished ? " (Successfully)" : approvedNurseIds.length > 0 ? " (Partially Successfully)" : " (Not Successfully)"}
					</Typography>
				);
			case AgentStaffActionStatus.Paused:
				return t("status.paused");
			case AgentStaffActionStatus.NotInitiated:
				return "Waiting for agent to start...";
			default:
				return t("status.running");
		}
	}, [agentStatus, isAccomplished, approvedNurseIds, t]);

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, display: "flex", gap: 0, alignItems: "center", justifyContent: "space-between" }}>
				<Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px" }}>
						{titleText}
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 400, color: INK.SECONDARY, opacity: 0.75, fontSize: "14px" }}>
						searching {neededNurses === undefined ? "—" : approvedNurseIds.length ? `${approvedNurseIds.length} / ${neededNurses}` : neededNurses} staff
					</Typography>
				</Box>
				<Box>{statusBox}</Box>
			</CardContent>
		</Card>
	);
};
